import {
  document_match_key,
  fetch_requestor_entity_profiles,
  is_image_document,
  is_pdf_document,
  submit_requestor_form_answers,
  to_number,
  upload_requestor_application_documents,
} from './request_portal_helpers';
import { hydrate_case_record_details, hydrate_case_record_display } from './request_portal_case_mapping';
import { clear_auth_storage } from '@/utils/auth/session_manager';

function visible_case_types(case_type_options = []) {
  return case_type_options.filter((item) => to_number(item?.id));
}

function fetch_case_type_records(context, case_type, options = {}) {
  return safe_fetch_case_type_records(context, case_type, options)
    .then((response) => ({ case_type, records: response.data || [] }));
}

async function safe_fetch_case_type_records(context, case_type, options = {}) {
  try {
    return await fetch_requestor_entity_profiles(case_type_records_query(context, case_type), options);
  } catch {
    return { data: [] };
  }
}

function case_type_records_query(context, case_type) {
  return {
    entity_type_id: case_type.id,
    page_size: 50,
    page: 1,
    current_user_only: true,
    ...project_filter(context),
  };
}

function project_filter(context) {
  return context.project_id ? { project_id: context.project_id } : {};
}

async function map_case_batches(context, profile_batches = []) {
  const cases = [];

  for (const batch of profile_batches) {
    for (const record of batch.records) {
      cases.push(await safe_map_case_record(context, record, batch.case_type));
    }
  }

  return cases;
}

async function safe_map_case_record(context, record = {}, case_type = {}) {
  try {
    return await context.map_backend_case_record(record, case_type);
  } catch {
    return null;
  }
}

function sorted_case_records(cases = []) {
  return cases
    .filter(Boolean)
    .sort((left, right) => Number(right.sort_time || 0) - Number(left.sort_time || 0));
}

function refreshed_selected_case(cases = [], selected_case = null) {
  if (!selected_case) {
    return null;
  }

  return cases.find((case_item) => Number(case_item?.id) === Number(selected_case?.id)) || null;
}

async function load_case_batches(context, options = {}) {
  return Promise.all(
    visible_case_types(context.case_type_options).map((case_type) =>
      fetch_case_type_records(context, case_type, options)
    )
  );
}

async function normalized_open_cases(context, options = {}) {
  const profile_batches = await load_case_batches(context, options);
  const cases = sorted_case_records(await map_case_batches(context, profile_batches));
  return Promise.all(cases.map((case_item) => safe_hydrate_case_display(context, case_item)));
}

async function safe_hydrate_case_display(context, case_item = {}) {
  try {
    return await hydrate_case_record_display(context, case_item);
  } catch {
    return case_item;
  }
}

function apply_open_cases_state(context, next_cases = []) {
  context.backend_open_cases = next_cases;
  const next_case_ids = next_cases.map((case_item) => String(case_item?.id || ''));
  const previous_collapsed = new Set(context.collapsed_case_ids.map(String));
  context.collapsed_case_ids = next_case_ids.filter((case_id) => previous_collapsed.has(case_id) || !context.last_open_cases_refresh_at);
  context.last_open_cases_refresh_at = Date.now();
}

function refresh_selected_previous_case(context, next_cases = []) {
  const refreshed_case = refreshed_selected_case(next_cases, context.selected_previous_case);
  if (refreshed_case) {
    context.selected_previous_case = refreshed_case;
  }
}

function open_cases_error_message(error) {
  return [
    error?.response?.data?.message,
    error?.message,
    'Failed to load open cases.',
  ].find(Boolean);
}

export default {
    async ensure_case_details(case_item = {}) {
      const target_case_id = Number(case_item?.id || case_item?.application_id);
      if (!target_case_id) {
        return null;
      }

      const existing_case = this.backend_open_cases.find((item) =>
        Number(item?.id || item?.application_id) === target_case_id
      );
      if (!existing_case || existing_case.details_loaded || existing_case.details_loading) {
        return existing_case || null;
      }

      const loading_case = { ...existing_case, details_loading: true };
      this.backend_open_cases = this.backend_open_cases.map((item) =>
        Number(item?.id || item?.application_id) === target_case_id ? loading_case : item
      );

      try {
        const hydrated_case = await hydrate_case_record_details(this, loading_case);
        this.backend_open_cases = this.backend_open_cases.map((item) =>
          Number(item?.id || item?.application_id) === target_case_id ? hydrated_case : item
        );
        this.maybe_begin_empty_case_section(hydrated_case);
        if (Number(this.selected_previous_case?.id || this.selected_previous_case?.application_id) === target_case_id) {
          this.selected_previous_case = hydrated_case;
        }
        return hydrated_case;
      } catch {
        const failed_case = { ...loading_case, details_loading: false };
        this.backend_open_cases = this.backend_open_cases.map((item) =>
          Number(item?.id || item?.application_id) === target_case_id ? failed_case : item
        );
        return failed_case;
      }
    },
    is_upload_documents_action(action = '') {
      return String(action).toLowerCase().includes('upload documents');
    },
    case_document_upload_targeted(case_item = {}) {
      return Number(this.selected_upload_case_id) === Number(case_item?.id || case_item?.application_id);
    },
    choose_case_document(case_item = {}) {
      if (this.uploading_case_document || !Number(case_item?.application_id)) {
        return;
      }

      this.selected_upload_case_id = Number(case_item?.id || case_item?.application_id);
      this.case_document_upload_error = '';
      this.case_document_upload_success = '';
      this.$refs.caseDocumentUploadInput?.click();
    },
    async upload_case_document(event) {
      const [file] = Array.from(event?.target?.files || []);
      clear_file_input(event?.target);

      const case_item = this.backend_open_cases.find((item) =>
        Number(item?.id || item?.application_id) === Number(this.selected_upload_case_id)
      );

      if (!file || !case_item?.application_id) {
        return;
      }

      this.uploading_case_document = true;
      this.case_document_upload_error = '';
      this.case_document_upload_success = '';

      try {
        await upload_requestor_application_documents({
          application_id: Number(case_item.application_id),
          files: [file],
          document_names: [file.name],
        });
        await this.load_open_cases();
        this.case_document_upload_success = 'Document uploaded successfully.';
      } catch (error) {
        this.case_document_upload_error = replacement_error_message(error);
      } finally {
        this.uploading_case_document = false;
      }
    },
    is_case_collapsed(case_item = {}) {
      return this.collapsed_case_ids.map(String).includes(String(case_item?.id || ''));
    },
    is_case_expanded(case_item = {}) {
      return !this.is_case_collapsed(case_item);
    },
    async toggle_case(case_item = {}) {
      const case_id = String(case_item?.id || '');
      if (!case_id) {
        return;
      }

      const collapsed_ids = new Set(this.collapsed_case_ids.map(String));
      if (collapsed_ids.has(case_id)) {
        collapsed_ids.delete(case_id);
        await this.ensure_case_details(case_item);
      } else {
        collapsed_ids.add(case_id);
      }

      this.collapsed_case_ids = Array.from(collapsed_ids);
    },
    answer_section_facts(answer_section = {}) {
      return (Array.isArray(answer_section?.facts) ? answer_section.facts : [])
        .filter((fact) => !fact?.document);
    },
    answer_section_documents(answer_section = {}, case_item = {}) {
      const existing_documents = new Set(
        (Array.isArray(case_item?.documents) ? case_item.documents : [])
          .map(document_match_key)
          .filter(Boolean)
      );

      return (Array.isArray(answer_section?.facts) ? answer_section.facts : [])
        .map((fact) => fact?.document)
        .filter((document) => document && !existing_documents.has(document_match_key(document)));
    },
    has_visible_answer_section(answer_section = {}, case_item = {}) {
      return Boolean(
        this.answer_section_facts(answer_section).length
        || this.answer_section_documents(answer_section, case_item).length
      );
    },
    load_open_cases(options = {}) {
      return load_open_cases_for_context(this, options);
    },
    open_document_preview(document) {
      this.selected_document = document;
      this.document_replace_error = '';
      this.document_replace_success = '';
    },
    close_document_preview() {
      this.selected_document = null;
      this.replacing_document = false;
      this.document_replace_error = '';
      this.document_replace_success = '';
      clear_file_input(this.$refs.replacementDocumentInput);
    },
    async open_previous_case_modal(case_item = {}) {
      this.selected_previous_case = case_item;
      await this.ensure_case_details(case_item);
    },
    close_previous_case_modal() {
      this.selected_previous_case = null;
    },
    can_preview_document(document = {}) {
      return Boolean(document?.url && (is_image_document(document) || is_pdf_document(document)));
    },
    is_image_document(document = {}) {
      return is_image_document(document);
    },
    is_pdf_document(document = {}) {
      return is_pdf_document(document);
    },
    download_document(document = {}) {
      if (!document?.url || typeof window === 'undefined') {
        return;
      }
      window.open(document.url, '_blank', 'noopener,noreferrer');
    },
    can_resubmit_document(document = {}) {
      if (String(document?.verification_state || '').toLowerCase() !== 'rejected') {
        return false;
      }

      return this.active_cases.some((case_item) =>
        Number(case_item?.application_id) === Number(document?.application_id)
      );
    },
    choose_replacement_document() {
      if (!this.selected_document || this.replacing_document || !this.can_resubmit_document(this.selected_document)) {
        return;
      }

      this.$refs.replacementDocumentInput?.click();
    },
    async replace_selected_document(event) {
      const [file] = Array.from(event?.target?.files || []);
      clear_file_input(event?.target);

      if (!file || !this.selected_document || !this.can_resubmit_document(this.selected_document)) {
        return;
      }

      this.replacing_document = true;
      this.document_replace_error = '';
      this.document_replace_success = '';

      try {
        await submit_document_replacement(this.selected_document, file);
        await this.load_open_cases();

        const refreshed_document = find_document_across_cases(this.backend_open_cases, this.selected_document);
        this.selected_document = refreshed_document || this.selected_document;
        this.document_replace_success = 'Replacement document uploaded. It is now waiting for review.';
      } catch (error) {
        this.document_replace_error = replacement_error_message(error);
      } finally {
        this.replacing_document = false;
      }
    },
    sign_out() {
      clear_auth_storage();
      this.$store.dispatch('resetStore');
      this.$router.push({ name: 'UserLogin' });
    },
    async open_new_case_modal() {
      this.clear_modal_state();
      this.show_new_case_modal = true;
      if (!this.case_type_options.length && !this.loading_case_types) {
        await this.load_case_types();
      }
    },
    close_new_case_modal() {
      this.show_new_case_modal = false;
      this.clear_modal_state();
    },
};

async function load_open_cases_for_context(context, options = {}) {
  context.loading_open_cases = true;
  context.portal_error = '';

  try {
    const next_cases = await normalized_open_cases(context, options);
    apply_open_cases_state(context, next_cases);
    refresh_selected_previous_case(context, next_cases);
  } catch (error) {
    context.backend_open_cases = [];
    context.show_error_message(open_cases_error_message(error));
  } finally {
    context.loading_open_cases = false;
  }
}

function clear_file_input(input) {
  if (input) {
    input.value = '';
  }
}

async function submit_document_replacement(document = {}, file) {
  if (String(document?.source || '').toLowerCase() === 'additional') {
    return upload_requestor_application_documents({
      application_id: Number(document?.application_id),
      files: [file],
      document_names: [document?.question || document?.title || file.name],
    });
  }

  return submit_requestor_form_answers({
    application_form_id: Number(document?.application_form_id),
    application_id: Number(document?.application_id),
    answers: [
      {
        section_question_id: Number(document?.section_question_id),
        question_id: Number(document?.question_id || 0) || null,
        answer_text: file.name,
        file,
      },
    ],
  });
}

function replacement_error_message(error) {
  return [
    error?.response?.data?.message,
    error?.response?.data?.detail,
    error?.message,
    'Failed to upload the replacement document.',
  ].find(Boolean);
}

function matching_document(document = {}, selected_document = {}) {
  return (
    String(document?.source || '') === String(selected_document?.source || '')
    && String(document?.additional_document_id || document?.id || '') === String(selected_document?.additional_document_id || selected_document?.id || '')
    && String(document?.application_form_id || '') === String(selected_document?.application_form_id || '')
    && String(document?.section_question_id || '') === String(selected_document?.section_question_id || '')
    && String(document?.question_id || '') === String(selected_document?.question_id || '')
  );
}

function find_document_across_cases(cases = [], selected_document = {}) {
  for (const case_item of Array.isArray(cases) ? cases : []) {
    const documents = Array.isArray(case_item?.documents) ? case_item.documents : [];
    const matched_document = documents.find((document) => matching_document(document, selected_document));
    if (matched_document) {
      return matched_document;
    }
  }

  return null;
}
